import { useEffect, useRef, useState, type UIEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogBody,
  DialogFooter,
  DialogClose,
  Button,
} from '@vnetwork/ui/primitives';
import {
  TERMS_SECTIONS,
  COMPANY_NAME,
  TERMS_EFFECTIVE_DATE,
} from './terms-content';

interface TermsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user reaches the end and confirms agreement. */
  onAgree: () => void;
}

const BOTTOM_THRESHOLD = 24; // px slack so the last line counts as "the end".

/**
 * Terms of Service reader. The "I Agree" action stays disabled until the user
 * has scrolled to the bottom of the document.
 */
export function TermsDialog({ open, onOpenChange, onAgree }: TermsDialogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [reachedEnd, setReachedEnd] = useState(false);

  // Reset on open; if the content is too short to scroll, unlock immediately.
  useEffect(() => {
    if (!open) {
      setReachedEnd(false);
      return;
    }
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight + BOTTOM_THRESHOLD) {
      setReachedEnd(true);
    }
  }, [open]);

  const handleScroll = (e: UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - BOTTOM_THRESHOLD) {
      setReachedEnd(true);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle>Terms of Service</DialogTitle>
          <DialogDescription>
            {COMPANY_NAME}
            {TERMS_EFFECTIVE_DATE ? ` · Effective ${TERMS_EFFECTIVE_DATE}` : ''}
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            style={{
              maxHeight: '60vh',
              overflowY: 'auto',
              paddingRight: 'var(--vn-space-3)',
              display: 'grid',
              gap: 'var(--vn-space-5)',
            }}
          >
            {TERMS_SECTIONS.map((section) => (
              <section key={section.heading} style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 'var(--vn-font-size-lg)',
                    fontWeight: 'var(--vn-font-weight-semibold)',
                  }}
                >
                  {section.heading}
                </h3>
                {section.paragraphs.map((p, i) => (
                  <p
                    key={i}
                    style={{
                      margin: 0,
                      fontSize: 'var(--vn-font-size-md)',
                      lineHeight: 'var(--vn-line-height-normal)',
                      color: 'var(--vn-fg-muted)',
                    }}
                  >
                    {p}
                  </p>
                ))}
              </section>
            ))}
            <p
              style={{
                textAlign: 'center',
                fontSize: 'var(--vn-font-size-md)',
                color: 'var(--vn-fg-subtle)',
                margin: 0,
              }}
            >
              — End of Terms —
            </p>
          </div>
        </DialogBody>

        <DialogFooter>
          {!reachedEnd ? (
            <span
              style={{
                marginRight: 'auto',
                fontSize: 'var(--vn-font-size-md)',
                color: 'var(--vn-fg-subtle)',
              }}
            >
              Scroll to the bottom to continue
            </span>
          ) : null}
          <DialogClose asChild>
            <Button variant="secondary">Cancel</Button>
          </DialogClose>
          <Button disabled={!reachedEnd} onClick={onAgree}>
            I Agree
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
